/** @jsxImportSource theme-ui */
import {
  Column,
  COLOR,
  HEADING_SIZE,
  Heading,
  Markdown,
  Paragraph,
  PARAGRAPH_SIZE,
  Row,
  ArrowLink,
  useMediaQuery,
  useTheme,
} from '@ndlib/component-library'
import Moment from 'moment'
import MaximumReadableWidth from '../../layout/MaximumReadableWidth'
import { NewsShareLinks } from '../../components/News/ShareLinks'
import VideoContent from './VideoContent'

export type GatsbyNewsArticle = Queries.NewsArticleQuery["strapiNews"]

export const BaseNewsPage: React.FC<{
  data: Queries.NewsArticleQuery
}> = ({ data }) => {
  const { breakpoint } = useMediaQuery()
  const theme = useTheme()

  const dateByline = () => {
    const byline = data.strapiNews?.author ? `By ${data.strapiNews?.author}` : ''
    const connector = data.strapiNews?.author && data.strapiNews?.publishedDate ? ' | ' : ''
    const date = data.strapiNews?.publishedDate ? Moment(data.strapiNews?.publishedDate).format("MMMM D, YYYY") : ''
    if (!byline && !date) {
      return null
    }
    return (
      <Paragraph size={PARAGRAPH_SIZE.SM} sx={{mr: 5, mt: 0}}>
        {byline}{connector}{date}
      </Paragraph>
    )
  }

  return (
    <MaximumReadableWidth>
      <Row>
        <Column sx={{ mt: 5, mr: 6, maxWidth: '805px' }}>
          <Heading
            underline={true}
            size={breakpoint > 1 ? HEADING_SIZE.XL : HEADING_SIZE.MD}>
            {data.strapiNews?.title}
          </Heading>
          <Row align="center" sx={{mt: 5}}>
            {dateByline()}
            <NewsShareLinks newsItem={data?.strapiNews!} />
          </Row>

          <Column sx={{
            pt: 5,
            width: ['100%', '100%', 800],
            alignItems: 'flex-start'
          }}>
            {data.strapiNews?.image?.url && (
              <Column sx={{ p: 1, boxShadow: theme.boxShadow.EMPHASIZED, mb: 3 }}>
                <img
                  src={data.strapiNews?.image?.url!}
                  alt={data.strapiNews?.title!}
                  style={{
                    width: breakpoint <= 1 ? '100%' : undefined,
                    height: breakpoint > 1 ? '450px' : undefined,
                  }}
                />
              </Column>
            )}
            <Markdown
              sx={{
                width: '100%',
                "& figure": {
                  "&.image": {
                    margin: '1em',
                    "&.image-style-align-left": {
                      float: 'left',
                      margin: '0 1em 1em 0',
                    },
                    "&.image-style-align-right": {
                      float: 'right',
                    },
                    "&.image.image-style-side": {
                    display: 'block',
                    float: 'right',
                    margin: '0 0 1em 1em',
                    maxWidth: '50%'
                  },
                  },
                  "&.image img": {
                    margin: '1em auto',
                  },
                  "&.media iframe": {
                    width: '100%',
                    height: '650px',
                  },
                },
                "img": {
                  maxWidth: 800,
                  height: 'auto',
                  display: 'block',
                  width: breakpoint < 1 ? '100%' : '',
                  margin: '1em 0',
                  "&.image-style-align-left": {
                    float: 'left',
                    margin: '0 1em 1em 0',
                  },
                  "&.image-style-align-right": {
                    float: 'right',
                    margin: '1em',
                  },
                  "&.image-style-side": {
                    display: 'block',
                    float: 'right',
                    margin: '0 0 1em 1em',
                    maxWidth: '50%'
                  }
                },
                "p": {
                  "&.align-right": {
                    textAlign: 'right',
                  },
                  "&.align-center": {
                    textAlign: 'center',
                  }
,                }
              }}
              content={data.strapiNews?.content?.data?.content!}
              enableHtml
            />
            <hr sx={{borderBottomColor: COLOR.BLACK, width: '100%'}} />
            {data.strapiNews?.contactUsPubInfo?.data?.contactUsPubInfo && (
              <Markdown
                content={data.strapiNews.contactUsPubInfo.data.contactUsPubInfo}
                enableHtml
              />
            )}
            {data.strapiNews?.video?.videoUrl && <VideoContent {...data.strapiNews.video} />}
            <Row sx={{mt: 4}}>
              <ArrowLink to="/news">View all News</ArrowLink>
            </Row>
          </Column>
        </Column>
      </Row>
    </MaximumReadableWidth>
  )
}
